import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import {
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  QuestionIcon,
  TargetIcon,
  XIcon,
  LockIcon,
  WarningIcon,
  TrophyIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import SyllabusModal from '../modals/SyllabusModal';

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  const datePart = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });

  const weekday = date.toLocaleDateString('en-GB', {
    weekday: 'short',
  });

  const timePart = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  return `${datePart} (${weekday}), ${timePart}`;
};

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        return null;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
};

const formatCountdown = (timeLeft) => {
  if (!timeLeft) return null;

  const parts = [];
  if (timeLeft.days > 0) parts.push(`${timeLeft.days}d`);
  if (timeLeft.hours > 0) parts.push(`${timeLeft.hours}h`);
  if (timeLeft.minutes > 0) parts.push(`${timeLeft.minutes}m`);
  parts.push(`${timeLeft.seconds}s`);

  return parts.join(' ');
};

export default function TestListingCard({
  test,
  index,
  currentPage = 1,
  packId,
  isPackPurchased = true,
  navigate,
  selectedCategoryData,
}) {
  const [showSyllabusModal, setShowSyllabusModal] = useState(false);
  const [syllabusModalContent, setSyllabusModalContent] = useState({ title: '', syllabus: '' });

  const analysisAvailable = test.isAnalysisAvailable;
  const testEnded = test.isTestExpired;

  const countdown = useCountdown(test.startDate);
  const isReleased = countdown === null;

  let status;
  if (!isReleased) status = 'not-released';
  else if (test.isTestInProgress) status = 'in-progress';
  else if (test.isTestAttemptedByUser) status = 'completed';
  else status = 'available';

  const handleViewSyllabus = (title, syllabusHtml) => {
    setSyllabusModalContent({ title, syllabus: syllabusHtml });
    setShowSyllabusModal(true);
  };

  const showUpcomingTestToast = (test) => {
    toast.custom(
      (t) => (
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-4 shadow-lg max-w-md">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ClockIcon className="text-[var(--q3-accent-normal)]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-[var(--q3-accent-normal)]">
                  {test.title} will be available on {formatDateTime(test?.startDate)}
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 cursor-pointer">
              <Button
                variant={'outline'}
                className="inline-flex !bg-[var(--q3-surface-default)] shadow-none border-none text-[var(--q3-danger-normal)] hover:text-[var(--q3-danger-dark)]"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.remove(t.id);
                }}
              >
                <span className="sr-only">Close</span>
                <XIcon />
              </Button>
            </div>
          </div>
        </div>
      ),
      {
        position: 'bottom-center',
        duration: 3000,
      },
    );
  };

  const showTestEndedToast = (test) => {
    toast.custom(
      (t) => (
        <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-4 shadow-lg max-w-md">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ClockIcon className="text-[var(--q3-accent-normal)]" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-[var(--q3-accent-normal)]">
                  {status === 'completed' || status === 'in-progress'
                    ? `Result will be available on ${formatDateTime(test?.detailAccessDate)}`
                    : `${test.title} has ended on ${formatDateTime(test?.endDate)}`}
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 cursor-pointer">
              <Button
                variant={'outline'}
                className="inline-flex !bg-[var(--q3-surface-default)] shadow-none border-none text-[var(--q3-danger-normal)] hover:text-[var(--q3-danger-dark)]"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.remove(t.id);
                }}
              >
                <span className="sr-only">Close</span>
                <XIcon />
              </Button>
            </div>
          </div>
        </div>
      ),
      {
        position: 'bottom-center',
        duration: 3000,
      },
    );
  };

  const handleTestNavigation = (testId, path) => {
    const url = `/${packId}/${testId}/${path}?categoryId=${selectedCategoryData?.groupId || selectedCategoryData?.title}`;
    navigate(url, {
      state: { selectedCategoryId: selectedCategoryData?.groupId },
    });
  };

  return (
    <>
      <Card
        className="border border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)] rounded-lg shadow-none py-0 overflow-hidden q3-interaction-scale-up cursor-pointer"
        onClick={() =>
          status === 'not-released'
            ? showUpcomingTestToast(test)
            : status === 'completed' && analysisAvailable
              ? navigate(`/${packId}/${test._id}/analysis`)
              : !testEnded && status !== 'completed'
                ? navigate(`/${packId}/${test._id}/details`)
                : showTestEndedToast(test)
        }
      >
        <div className="flex flex-col w-full">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 px-2 lg:px-5 py-2 lg:py-6">
            <div className="flex items-center w-full lg:w-fit gap-5">
              <div
                className={`hidden lg:flex items-center justify-center w-4 h-4 ${status === 'completed' || (status === 'in-progress' && testEnded) ? 'bg-[var(--q3-success-normal)]' : ''} rounded-full`}
              >
                {isPackPurchased ? (
                  status === 'completed' || (status === 'in-progress' && testEnded) ? (
                    <CheckIcon size={10} weight="bold" color="var(--q3-success-light)" />
                  ) : (
                    <span className="font-medium text-[var(--q3-neutral-light)]">
                      {index + 1 + (currentPage - 1) * 10}
                    </span>
                  )
                ) : (
                  <LockIcon size={16} weight="bold" color="var(--q3-danger-normal)" />
                )}
              </div>
              <div className="flex flex-col w-full lg:w-fit">
                <div className="flex items-start justify-start gap-2 w-full lg:w-fit text-left">
                  <h3 className="font-bold text-md">
                    {test.title}{' '}
                    {test.tags &&
                      test.tags.map((item, idx) => (
                        <Badge
                          key={idx}
                          style={{
                            backgroundColor: item.background,
                            color: item.textColor,
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            borderRadius: '6px',
                          }}
                          className="leading-none"
                        >
                          {item.title}
                        </Badge>
                      ))}
                  </h3>
                </div>

                <div className="flex items-center flex-wrap gap-2 mt-1 text-sm text-gray-500">
                  <div className="flex items-center gap-1">{test.description}</div>
                </div>
              </div>
            </div>

            <div className="flex w-full lg:w-fit flex-row gap-2">
              {status === 'not-released' && (
                <>
                  {/* <Button
                    className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewSyllabus(test.title, test.syllabus);
                    }}
                  >
                    View Syllabus
                  </Button> */}
                  <Button className="flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] opacity-30 cursor-not-allowed">
                    Attempt Test
                  </Button>
                </>
              )}

              {status === 'available' && (
                <>
                  {/* <Button
                    className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewSyllabus(test.title, test.syllabus);
                    }}
                  >
                    View Syllabus
                  </Button> */}
                  <Button
                    className={`flex-1 rounded-full bg-[var(--q3-accent-normal)] hover:bg-[var(--q3-accent-dark)] text-[var(--q3-neutral-default_inverted)] ${testEnded ? 'opacity-30 cursor-not-allowed' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (testEnded) return;
                      handleTestNavigation(test._id, 'details');
                    }}
                  >
                    Attempt Test
                  </Button>
                </>
              )}

              {status === 'in-progress' && !testEnded && (
                <>
                  {/* <Button
                    className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewSyllabus(test.title, test.syllabus);
                    }}
                  >
                    View Syllabus
                  </Button> */}
                  <Button
                    className={`flex-1 rounded-full bg-[var(--q3-yellow-normal)] hover:bg-[var(--q3-yellow-dark)] text-[var(--q3-neutral-default_inverted)] ${testEnded ? 'opacity-30 cursor-not-allowed' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (testEnded) return;
                      handleTestNavigation(test._id, 'details');
                    }}
                  >
                    Resume Test
                  </Button>
                </>
              )}

              {(status === 'completed' || (status === 'in-progress' && testEnded)) && (
                <>
                  <Button
                    className="flex-1 rounded-full text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dimmest)]"
                    disabled={!analysisAvailable}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/${packId}/${test._id}/solutions?categoryId=${selectedCategoryData?.groupId || selectedCategoryData?.title}`,
                        {
                          state: { selectedCategoryId: selectedCategoryData?.groupId },
                        },
                      );
                    }}
                  >
                    View Solution
                  </Button>
                  <Button
                    className="flex-1 rounded-full bg-[var(--q3-success-normal)] hover:bg-[var(--q3-success-dark)] text-[var(--q3-neutral-default_inverted)]"
                    disabled={!analysisAvailable}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestNavigation(test._id, 'analysis');
                    }}
                  >
                    View Result
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="px-2 lg:pl-14 lg:pr-4 flex justify-between items-center py-2 bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-light)] flex-wrap">
            {status === 'not-released' && !testEnded && (
              <div className="flex justify-between items-center w-full flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  {/* <div className="flex items-center gap-1 text-sm">
                    <QuestionIcon size={20} /> <strong>{test.totalQuestions} Qs</strong>
                  </div> */}
                  <div className="flex items-center gap-1 text-sm">
                    <ClockIcon size={20} />
                    <strong>{test.totalTime} Min</strong>
                  </div>
                  {/* <div className="flex items-center gap-1 text-sm">
                    <CheckCircleIcon size={20} /> Total Marks: <strong>{test.totalMarks}</strong>
                  </div> */}
                </div>

                {status === 'not-released' && (
                  <span className="w-full sm:w-auto flex justify-start sm:justify-end">
                    <Badge className="bg-[var(--q3-surface-default)] border-[var(--q3-stroke-light)] text-[var(--q3-accent-normal)] text-sm font-medium whitespace-nowrap">
                      Starts in: <strong>{formatCountdown(countdown)}</strong>
                    </Badge>
                  </span>
                )}
              </div>
            )}

            {status === 'in-progress' && !testEnded && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-sm">
                  <ClockIcon size={20} /> Time Remaining:
                  <strong>{test.timeLeft} Min</strong>
                </div>
              </div>
            )}

            {(status === 'completed' || (status === 'in-progress' && testEnded)) && !analysisAvailable && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-sm">
                  <ClockIcon size={20} /> Result will be available on{' '}
                  <strong>{formatDateTime(test?.detailAccessDate)}</strong>
                </div>
              </div>
            )}

            {status !== 'in-progress' && status !== 'completed' && testEnded && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-sm">
                  <WarningIcon size={20} /> You missed to attempt the test
                </div>
              </div>
            )}

            {status === 'completed' && analysisAvailable && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-sm">
                  <TrophyIcon size={20} /> Result is announced
                </div>
              </div>
            )}
            {status === 'available' && !testEnded && (
              <div className="flex justify-between items-center w-full flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1 text-sm">
                    <QuestionIcon size={20} /> <strong>{test.totalQuestions} Qs</strong>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <ClockIcon size={20} />
                    <strong>{test.totalTime} Min</strong>
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <CheckCircleIcon size={20} /> Total Marks: <strong>{test.totalMarks}</strong>
                  </div>
                </div>

                {/* {new Date(test?.endDate) < new Date() && (
                  <span className="w-full sm:w-auto flex justify-center sm:justify-end">
                    <Badge className="bg-[var(--q3-surface-default)] border-[var(--q3-stroke-light)] text-[var(--q3-accent-normal)] text-sm font-medium whitespace-nowrap">
                      Test Ended On: <strong>{formatDateTime(test?.endDate)}</strong>
                    </Badge>
                  </span>
                )} */}
              </div>
            )}
          </div>
        </div>
      </Card>

      <SyllabusModal
        open={showSyllabusModal}
        onOpenChange={setShowSyllabusModal}
        dialogContent={syllabusModalContent}
      />
    </>
  );
}
